import React from 'react';
import { Modal, Form, Input, Select, Button, Spin, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import USFaxNumberInput from 'components/form/USFaxNumberInput';
import type { FaxContact, FaxContactVo } from 'types/eFax';
import { CONTACT_TAG_OPTIONS } from 'types/eFax';
import { faxContactGet } from 'api/fax';
import FormValidationHelper from 'utils/validation';
import {
    faxNanpDigitsForInput,
    isFaxContactServerId,
    isFaxScheduleSuccess,
    mapFaxContactVoToFaxContact,
    normalizeFaxForApi,
} from 'utils/eFaxScheduleRows';
import s from './s.module.less';

interface Props {
    open: boolean;
    onClose: () => void;
    /** Resolve when persist succeeds so the modal can close; reject or throw to keep it open. */
    onSave: (contact: FaxContact) => void | Promise<void>;
    editingContact?: FaxContact;
    initialFaxNumber?: string;
}

const AddContactModal: React.FC<Props> = ({ open, onClose, onSave, editingContact, initialFaxNumber }) => {
    const [form] = Form.useForm();
    const [detailLoading, setDetailLoading] = React.useState(false);
    const isEditing = !!editingContact;
    const nameVal = Form.useWatch('name', form);
    const faxVal = Form.useWatch('faxNumber', form);

    const canSubmit =
        Boolean(String(nameVal ?? '').trim()) &&
        Boolean(String(faxVal ?? '').trim()) &&
        !detailLoading;

    React.useEffect(() => {
        if (!open) {
            setDetailLoading(false);
            return;
        }
        if (!editingContact) {
            form.resetFields();
            if (initialFaxNumber) {
                form.setFieldValue('faxNumber', faxNanpDigitsForInput(initialFaxNumber));
            }
            setDetailLoading(false);
            return;
        }

        form.setFieldsValue({
            name: editingContact.name,
            faxNumber: faxNanpDigitsForInput(editingContact.faxNumber),
            tag: editingContact.tag,
            notes: editingContact.notes,
        });

        if (!isFaxContactServerId(editingContact.id)) {
            setDetailLoading(false);
            return;
        }

        let cancelled = false;
        setDetailLoading(true);
        (async () => {
            const result = await faxContactGet(Number(editingContact.id));
            const envelope = result?.data as { code?: number; message?: string; data?: unknown } | undefined;
            if (cancelled) return;
            setDetailLoading(false);
            if (result?.error || !envelope || !isFaxScheduleSuccess(envelope)) {
                message.error(
                    envelope?.message ||
                        (typeof result?.error === 'string' ? result.error : null) ||
                        'Failed to load contact',
                );
                return;
            }
            const vo = envelope.data as FaxContactVo | null | undefined;
            if (!vo || cancelled) return;
            const c = mapFaxContactVoToFaxContact(vo);
            form.setFieldsValue({
                name: c.name,
                faxNumber: faxNanpDigitsForInput(c.faxNumber),
                tag: c.tag,
                notes: c.notes,
            });
        })();

        return () => {
            cancelled = true;
        };
    }, [open, editingContact, initialFaxNumber, form]);

    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        try {
            const values = await form.validateFields();
            const notesTrimmed = typeof values.notes === 'string' ? values.notes.trim() : '';
            const resolvedName = String(values.name ?? '').trim();
            const contact: FaxContact = {
                id: editingContact?.id || `contact_${Date.now()}`,
                name: resolvedName,
                faxNumber: normalizeFaxForApi(values.faxNumber),
                tag: values.tag,
                notes: notesTrimmed || undefined,
            };
            await onSave(contact);
            form.resetFields();
            onClose();
        } catch {
            // validation failed or onSave rejected
        }
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            footer={null}
            closable={false}
            width={543}
            destroyOnClose
            wrapClassName={s.modalWrap}
            maskClosable
        >
            <div className={s.root}>
                <div className={s.header}>
                    <span className={s.title}>{isEditing ? 'Edit contact' : 'Add new contact'}</span>
                    <button type="button" className={s.closeBtn} onClick={handleClose} aria-label="Close">
                        <CloseOutlined />
                    </button>
                </div>
                <div className={s.body}>
                    <Spin spinning={detailLoading}>
                        <Form form={form} layout="vertical" requiredMark={false} className={s.form}>
                            <Form.Item
                                label="Name"
                                name="name"
                                rules={[{ required: true, message: 'Please enter a name' }]}
                            >
                                <Input placeholder="Enter name" size="large" disabled={detailLoading} />
                            </Form.Item>
                            <Form.Item
                                label="Fax number"
                                name="faxNumber"
                                validateFirst
                                rules={[
                                    { required: true, message: 'Please enter a fax number' },
                                    FormValidationHelper.validateUSPhoneNumber(
                                        'Please enter a valid 10-digit US fax number',
                                    ),
                                ]}
                            >
                                <USFaxNumberInput
                                    placeholder="(206) 555-1234"
                                    size="large"
                                    disabled={detailLoading}
                                />
                            </Form.Item>
                            <Form.Item
                                label={
                                    <span>
                                        Contact type <span className={s.optionalHint}>(Optional)</span>
                                    </span>
                                }
                                name="tag"
                            >
                                <Select
                                    placeholder="Select contact type"
                                    allowClear
                                    size="large"
                                    disabled={detailLoading}
                                    options={CONTACT_TAG_OPTIONS.map((opt) => ({ label: opt, value: opt }))}
                                />
                            </Form.Item>
                        </Form>
                    </Spin>
                </div>
                <div className={s.footer}>
                    <Button className={s.cancelBtn} onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        className={`${s.submitBtn} ${canSubmit ? s.submitBtnEnabled : s.submitBtnDisabled}`}
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                    >
                        {isEditing ? 'Save' : 'Create'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default AddContactModal;
